import {ChangeDetectionStrategy, Component, computed, input,} from '@angular/core';
import {resolveIconColor} from './app-icon-colors';
import type {IconColor} from './app-icon-colors';
import {resolveAppIcon} from './app-icon.registry';
import type {AppIconName, AppIconWeight} from './app-icon.types';
/**
 * Icona inline da [Phosphor Icons](https://phosphoricons.com/) con `currentColor`.
 *
 * Solo le icone registrate in `app-icon.registry.ts` finiscono nel bundle.
 *
 * @example Decorativa (suffix campo, freccia CTA)
 * ```html
 * <app-icon name="MapPin" [size]="20" aria-hidden="true" />
 * <app-icon name="CalendarBlank" [size]="20" color="dark-grey" aria-hidden="true" />
 * ```
 *
 * @example Con significato per screen reader
 * ```html
 * <app-icon name="Users" ariaLabel="Viaggiatori" [ariaHidden]="false" />
 * ```
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  templateUrl: './app-icon.component.html',
  styleUrl: './app-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'app-icon-host',
    '[style.--app-icon-size.px]': 'size()',
    '[style.color]': 'resolvedColor()',
  },
})
export class AppIconComponent {
  /** Nome PascalCase Phosphor (es. `CaretDown`, `MapPin`). */
  readonly name = input.required<AppIconName>();
  /** Lato in px (width e height). Default 24. */
  readonly size = input(24);
  /** Peso Phosphor; oggi solo `regular` è popolato nel registry. */
  readonly weight = input<AppIconWeight>('regular');
  /** `true` per icone decorative (default). */
  readonly ariaHidden = input(true, {
    transform: (v: boolean | string) => v === true || v === '',
  });
  /** Se valorizzato, l'icona diventa significativa (`role="img"`). */
  readonly ariaLabel = input<string | null>(null);
  /** Token colore design system; omesso = `inherit` dal contesto. */
  readonly color = input<IconColor | null>(null);

  protected readonly definition = computed(() =>
    resolveAppIcon(this.name(), this.weight())
  );

  protected readonly resolvedColor = computed(() => resolveIconColor(this.color()));

  protected readonly isMeaningful = computed(
    () => !this.ariaHidden() && !!this.ariaLabel()?.trim()
  );
}
